/**
 * Pure helpers for the broker commission-rate (Add Rate) modal.
 *
 * Kept free of React so the mandatory-field validation can be unit-tested in
 * isolation. The "Specific product" scope exposes the full FIX-30 field set;
 * vehicle_type and the two dates are always mandatory, while group, subgroup
 * and policy_type are conditionally mandatory (FIX 10): required only when
 * their dropdowns actually offer options (per classificationFieldState).
 */

export interface BrokerRateFormState {
  // Always mandatory (specific product)
  vehicle_type: string;
  effective_from: string;
  effective_until: string;

  // Conditionally mandatory classification (required when options exist)
  group: string;
  subgroup: string;
  policy_type: string;

  // Optional dimensions
  rto: string;
  cc: string;
  gvw: string;
  fuel_type: string;
  od: string;
  tp: string;
  reward_to: string;
  reward_od: string;
  state: string;
  city: string;
  pincode: string;
  age: string;
  manufactured_year: string;
  seating_capacity: string;
  make: string;
  model: string;
  variant: string;
}

/** Human labels for the always-mandatory fields, used to build error messages. */
const ALWAYS_MANDATORY_LABELS: Record<string, string> = {
  vehicle_type: 'Vehicle Type',
  effective_from: 'From Date',
  effective_until: 'To Date',
};

/** Human labels for the conditionally mandatory classification fields. */
const CONDITIONAL_LABELS: Record<string, string> = {
  group: 'Group',
  subgroup: 'Subgroup',
  policy_type: 'Policy Type',
};

/** Which conditional classification fields are required for this submit. */
export interface BrokerRateConditionalRequirements {
  group: boolean;
  subgroup: boolean;
  policy_type: boolean;
}

/**
 * Validate the mandatory fields for a specific-product rate. Returns a map of
 * ``field -> message`` for every missing mandatory field (empty when valid).
 *
 * ``conditionallyRequired`` mirrors classificationFieldState().required per
 * field; the default keeps the historical behaviour (group/subgroup required,
 * policy_type optional).
 */
export function validateBrokerRateForm(
  form: BrokerRateFormState,
  conditionallyRequired: BrokerRateConditionalRequirements = {
    group: true,
    subgroup: true,
    policy_type: false,
  },
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [field, label] of Object.entries(ALWAYS_MANDATORY_LABELS)) {
    if (!form[field as keyof BrokerRateFormState]) {
      errors[field] = `${label} is required`;
    }
  }
  for (const [field, label] of Object.entries(CONDITIONAL_LABELS)) {
    if (
      conditionallyRequired[field as keyof BrokerRateConditionalRequirements] &&
      !form[field as keyof BrokerRateFormState]
    ) {
      errors[field] = `${label} is required`;
    }
  }
  return errors;
}
